import dayjs from 'dayjs';
import { toFullOption } from 'react-querybuilder';
import type { OptionGroup, RuleGroupType, RuleType } from 'react-querybuilder';

import {
  ValueLabel,
  ValueLabelGroup,
  AlarmRulesMetadata,
  DimAttributesMetadata,
  DataSourceAlarmMetadata,
  SelectedQueryBuilderField,
} from 'types/types';
import { VirtualizeAutocompleteOption } from '../../VirtualizeAutocomplete/VirtualizedAutocomplete';
import UserManager from 'utils/UserManager';

export interface QueryBuilderValidationResult {
  hasEmptyValue: boolean;
  validationMessage: string;
  hasKPIorPIorVendorCounter: boolean;
}

export enum InputType {
  NUMERIC = 'NUMERIC',
  STRING = 'STRING',
  SELECT = 'SELECT',
  INTEGER = 'INTEGER',
  DOUBLE = 'DOUBLE',
  ALPHANUMERIC = 'ALPHANUMERIC',
  DATE = 'DATE',
}

export enum AttributeType {
  KPI = 'KPI',
  PI = 'PI',
  DIMENSIONATTRIBUTE = 'DimensionAttribute',
  CUSTOM_KPI = 'CUSTOM_KPI',
}

export const CommaSeparatedSupportedLabels = ['IN', 'NOT IN'];
export const CommaSeparatedSupportedValues = ['#in', '#not_in'];

export enum DSPField {
  DIM_ATTRIBUTES = 'DIMATTRIBUTES',
  VENDOR_COUNTERS = 'VENDORCOUNTERS',
  KPIS = 'KPIS',
  PIS = 'PIS',
  CUSTOM_KPIS = 'CUSTOMKPIS',
}

export enum OperatorType {
  USER_ENTERED_VALUE = 'User_Entered_Value',
  SYSTEM_THRESHOLD = 'System_Threshold',
  AI_THRESHOLD = 'AI_Threshold',
  STANDARD_DEVIATION = 'Standard_Deviation',
  COMPARED_TO_AVERAGE = 'Compared_To_AVG',
  DELTA_AVERAGE = 'delta_with_average',
}

export const formatDate = (date: Date): string => {
  const newDate = getBrowserDateShiftedToUserTZ(date);

  // Manually formatting to match "yyyy-MM-ddThh:mm"
  const year = newDate.getFullYear();
  const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
  const day = newDate.getDate().toString().padStart(2, '0');
  const hours = newDate.getHours().toString().padStart(2, '0');
  const minutes = newDate.getMinutes().toString().padStart(2, '0');

  // Return the string in the required format
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const formatDateISO = (date: Date): string => {
  const newDate = getBrowserDateShiftedToUserTZ(date).toISOString();

  return newDate;
};
// eslint-disable-next-line no-undef
export const getBrowserDateShiftedToUserTZ = (date: Date) => {
  const userProps = UserManager.getProperties();
  const userTimeZone = userProps['timeZone']; // "Asia/Beirut";// userProps["timezone"]; "America/Argentina/Cordoba";
  const tz = date
    .toLocaleString('en', { timeZone: userTimeZone, timeStyle: 'long' })
    .split(' ')
    .slice(-1)[0];
  const dateString = date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false, //24-hour format
  });
  const userTimezoneOffset = -(
    Date.parse(`${dateString} UTC`) - Date.parse(`${dateString} ${tz}`)
  );
  const browserTimezoneOffset = -(date.getTimezoneOffset() * 60000);
  const nd = new Date(
    date.getTime() + browserTimezoneOffset + userTimezoneOffset
  );
  return nd;
};

export const hasSpecialExecutionPeriod = (
  ruleGroup: RuleGroupType
): boolean => {
  return ruleGroup?.rules?.some((ruleOrGroup) => {
    if ('rules' in ruleOrGroup && ruleOrGroup.rules !== null) {
      // If it's a nested rule group, recursively check its rules
      return hasSpecialExecutionPeriod(ruleOrGroup);
    } else {
      // If it's a rule, check the operator
      const rule = ruleOrGroup as RuleType;
      return (
        rule.operator.includes('standard_deviation') ||
        rule.operator.includes('compared_to_avg') ||
        rule.operator.includes('delta_with_average')
      );
    }
  });
};

export const removeAIThreshold = (field: OptionGroup[]): OptionGroup[] => {
  return field.map((item) => ({
    ...item,
    options: item.options.map((option) => ({
      ...option,
      operators: (option.operators as any[]).filter(
        (operator) => operator.label !== 'ai_threshold'
      ),
    })),
  }));
};

export const countFieldsInGroups = (
  fields: string[],
  ruleGroup: RuleGroupType
): Record<string, number> => {
  const fieldCounts: Record<string, number> = {};

  const countFields = (group: RuleGroupType) => {
    group.rules.forEach((ruleOrGroup) => {
      if ('rules' in ruleOrGroup && ruleOrGroup.rules !== null) {
        countFields(ruleOrGroup);
      } else {
        const field = (ruleOrGroup as RuleType).field;
        if (fields.includes(field)) {
          fieldCounts[field] = (fieldCounts[field] || 0) + 1;
        }
      }
    });
  };

  countFields(ruleGroup);

  return fieldCounts;
};

export const getUserEnteredValueOperators = (
  queryOperators: OptionGroup[]
): OptionGroup[] =>
  queryOperators.filter(
    (el) =>
      el.label.toLowerCase() === OperatorType.USER_ENTERED_VALUE.toLowerCase()
  );

export const getOptionByName = (ruleOperators: OptionGroup[], name: string) => {
  const filteredOptions = ruleOperators
    .map((group) => group.options.filter((option) => option.name === name))
    .flat();

  return filteredOptions.length > 0 ? filteredOptions[0] : null;
};

export const formatOptions = (options: any): VirtualizeAutocompleteOption[] => {
  if (options && options.length > 0 && 'options' in options[0]) {
    const formattedOptions = options.flatMap((group) =>
      group.options.map((option) => ({
        ...option,
        groupLabel: group.label,
      }))
    );
    return formattedOptions;
  }
  return options;
};

export const transformAttributesArray = (
  array: DimAttributesMetadata[],
  operators: any[]
): OptionGroup[] => {
  const userEnteredValueOperators = operators.filter(
    (el) =>
      el.label.toLowerCase() == OperatorType.USER_ENTERED_VALUE.toLowerCase()
  );
  const arr = array.map((item: any) => ({
    name: item.id,
    label: item.name || '',
    dataType: item.dataType,
    attributeType: item.attributeType,
    operators: getFilteredOperators(item, userEnteredValueOperators),
  }));

  const options: OptionGroup[] = [
    {
      label: 'DimAttributes',
      options: arr,
    },
  ].map((o) => toFullOption(o));

  return options;
};

export const transformRulesArray = (
  rules: AlarmRulesMetadata,
  ruleOperators: any[]
): OptionGroup[] => {
  const transformItem = (item: any) => ({
    name: item.id,
    label: item.name || '(null)',
    dataType: item.dataType,
    attributeType: item.attributeType,
    operators: getFilteredOperators(item, ruleOperators, true, true),
  });

  return Object.keys(rules).map((key) => ({
    label: key,
    options: rules[key].map(transformItem),
  }));
};

export const getFilteredOperators = (
  item: any,
  operators: any[],
  includeSelectInputType: boolean = false,
  skipDeltaOperators: boolean = false
) => {
  if (!item.isSystemThresholdEnabled) {
    operators = operators.filter((s) => s.label !== 'system_threshold');
  }
  if (!item.aiThresholdEnabled) {
    operators = operators.filter((s) => s.label !== 'ai_threshold');
  }
  if (
    skipDeltaOperators &&
    item.attributeType.toLowerCase() ==
      AttributeType.DIMENSIONATTRIBUTE.toLowerCase()
  ) {
    operators = operators.filter((s) => s.label !== 'delta_with_average');
  }
  if (item.dataType && item.dataType === InputType.STRING) {
    return operators
      .map((group) => ({
        ...group,
        options: group.options.filter(
          (option) =>
            option.valueEditorType.includes(InputType.STRING) ||
            option.valueEditorType.includes(InputType.ALPHANUMERIC) ||
            (includeSelectInputType &&
              option.valueEditorType.includes(InputType.SELECT))
        ),
      }))
      .filter((el) => el.options.length > 0);
  }
  if (item.dataType && item.dataType === InputType.SELECT) {
    return operators
      .map((group) => ({
        ...group,
        options: group.options.filter(
          (option) =>
            option.valueEditorType.includes(InputType.SELECT) ||
            option.valueEditorType.includes(InputType.ALPHANUMERIC)
        ),
      }))
      .filter((el) => el.options.length > 0);
  }
  if (item.dataType && item.dataType === InputType.DATE) {
    return operators
      .map((group) => ({
        ...group,
        options: group.options.filter((option) =>
          option.valueEditorType.includes(InputType.DATE)
        ),
      }))
      .filter((el) => el.options.length > 0);
  }

  return operators
    .map((group) => ({
      ...group,
      options: group.options.filter(
        (option) =>
          option.valueEditorType.includes(InputType.NUMERIC) ||
          option.valueEditorType.includes(InputType.ALPHANUMERIC) ||
          (includeSelectInputType &&
            option.valueEditorType.includes(InputType.SELECT))
      ),
    }))
    .filter((el) => el.options.length > 0);
};

export const mergeValueLabelGroups = (
  listItems: ValueLabelGroup[],
  valueLabelGroups: ValueLabelGroup[],
  removeDuplicates: boolean, //true for left side, false for right
  selectedRules?: SelectedQueryBuilderField
): ValueLabelGroup[] => {
  const isValueInValueLabelGroups = (value: string): boolean => {
    return valueLabelGroups.some(({ items }) =>
      items.some((valItem) => valItem.value === value)
    );
  };

  const mergedMap: Record<string, Record<string, ValueLabel>> = {};
  const duplicateSet = new Set<string>();

  const groupOrder = ['Dim Attributes', 'KPIs', 'PIs', 'KQIs', 'Custom KPIs'];

  const sortByGroup = (a: ValueLabelGroup, b: ValueLabelGroup): number => {
    const indexA = groupOrder.indexOf(a.group);
    const indexB = groupOrder.indexOf(b.group);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return a.group.localeCompare(b.group);
  };

  const mergeItems = (group: string, items: ValueLabel[]) => {
    if (!mergedMap[group]) {
      mergedMap[group] = {};
    }

    items.forEach((item) => {
      const existingItem = mergedMap[group][item.value];

      if (removeDuplicates) {
        if (existingItem || duplicateSet.has(item.value)) {
          duplicateSet.add(item.value);
          delete mergedMap[group][item.value];
        } else {
          if (!isValueInValueLabelGroups(item.value)) {
            mergedMap[group][item.value] = item;
          }
        }
      } else {
        if (!existingItem || existingItem.enableDeleteButton) {
          mergedMap[group][item.value] = item;
        }
      }
    });
  };

  listItems.forEach(({ group, items }) => {
    mergeItems(group, items);
  });

  valueLabelGroups.forEach(({ group, items }) => {
    mergeItems(group, items);
  });

  if (!removeDuplicates) {
    Object.entries(mergedMap).forEach(([group, itemsMap]) => {
      Object.keys(itemsMap).forEach((key) => {
        const item = itemsMap[key];

        if (!item.enableDeleteButton) {
          const isInTargetCriteria = selectedRules?.targetCriteria.includes(
            item.value
          );
          const isInRules = selectedRules?.rules.includes(item.value);

          if (!isInTargetCriteria && !isInRules) {
            delete itemsMap[key];
          }
        }
      });
    });
  }

  return Object.entries(mergedMap)
    .map(([group, itemsMap]) => ({
      group,
      items: Object.values(itemsMap),
    }))
    .filter(({ items }) => items.length > 0)
    .sort(sortByGroup);
};

export const transformDSPMetadataToLeft = (
  dataToTransform: DataSourceAlarmMetadata
): ValueLabelGroup[] => {
  const result: ValueLabelGroup[] = [];
  if (dataToTransform) {
    const dimAttributesGroup: ValueLabelGroup = {
      group: 'Dim Attributes',
      items: dataToTransform.dimAttributes.map((dim, index) => ({
        index: index,
        value: dim.id,
        label: dim.name,
        enableDeleteButton: false,
      })),
    };
    result.push(dimAttributesGroup);
  }
  return result;
};

export const hasQueryBuilderEmptyValues = (
  query: any,
  dataSourceMetadataForAlarm?: DataSourceAlarmMetadata,
  validation: QueryBuilderValidationResult = {
    hasEmptyValue: false,
    hasKPIorPIorVendorCounter: false,
    validationMessage: '',
  }
): QueryBuilderValidationResult => {
  const checkRule = (rule: any) => {
    if (!rule.field || !rule.operator) {
      validation.hasEmptyValue = true;
    }

    if (rule.value && !rule.value.value) {
      validation.hasEmptyValue = true;
    }
    if (
      rule.value?.type?.toUpperCase() == InputType.DATE &&
      CommaSeparatedSupportedValues.some((supportedLabel) =>
        rule.operator.toUpperCase().contains(supportedLabel.toUpperCase())
      )
    ) {
      const dateValues = rule.value?.value?.split(',').map((val) => val.trim());
      if (dateValues) {
        const isValidDateRange = dateValues.every((date) =>
          dayjs(date, 'yyyy-mm-dd').isValid()
        );
        if (!isValidDateRange) {
          validation.validationMessage =
            '[In] and [Not In] values for Date type should be a comma-separated dates (yyyy-mm-dd).'; // Flag as invalid if any date is invalid
        }
      } else {
        validation.validationMessage = '';
        validation.hasEmptyValue = true;
      }
    }

    if (checkNumericNonValidOperator(rule)) {
      validation.hasEmptyValue = true;
    }

    if (!checkFieldInMetadata(rule, dataSourceMetadataForAlarm)) {
      validation.hasEmptyValue = true;
    }

    if (rule.value === '') {
      validation.hasEmptyValue = true;
    }

    if (rule.value?.data?.attributeType) {
      const attributeType = rule.value.data.attributeType;
      if (['KPI', 'PI', 'CUSTOM_KPI'].includes(attributeType)) {
        validation.hasKPIorPIorVendorCounter = true;
      }
    }

    // Recursively check nested rules
    if (rule?.rules && rule?.rules?.length > 0) {
      rule?.rules?.forEach(checkRule);
    }
  };

  query?.rules?.forEach((rule: any) => {
    if ('rules' in rule && rule.rules != null) {
      hasQueryBuilderEmptyValues(rule, dataSourceMetadataForAlarm, validation);
    } else {
      checkRule(rule);
    }
  });

  return validation;
};

export const checkFieldInMetadata = (
  rule: any,
  dataSourceMetadataForAlarm?: DataSourceAlarmMetadata
): boolean => {
  const attributeType = rule?.value?.data?.attributeType;

  let metadataArray;
  switch (attributeType) {
    case 'KPI':
      metadataArray = dataSourceMetadataForAlarm?.kpis;
      break;
    case 'PI':
      metadataArray = dataSourceMetadataForAlarm?.pis;
      break;
    case 'KQI':
      metadataArray = dataSourceMetadataForAlarm?.kqis;
      break;
    case 'CUSTOM_KPI':
      metadataArray = dataSourceMetadataForAlarm?.customKpis;
      break;
    case 'DimensionAttribute':
      metadataArray = dataSourceMetadataForAlarm?.dimAttributes;
      break;
    default:
      return false;
  }

  return metadataArray?.some((item) => item.id === rule.field);
};

export const isQueryBuilderInInitMode = (query: any) => {
  if (query?.combinator === 'and' && query?.rules?.length === 0) {
    return true;
  }

  return false;
};

export const checkNumericNonValidOperator = (rule: any): boolean => {
  if (!rule.value || !rule.operator) {
    return false;
  }

  const isNumericType = ['NUMERIC', 'INTEGER', 'DOUBLE'].includes(
    rule.value.type?.toUpperCase() || ''
  );
  const isSystemThreshold = rule.operator
    .toUpperCase()
    .includes(OperatorType.SYSTEM_THRESHOLD.toUpperCase());
  const isAIThreshold = rule.operator
    .toUpperCase()
    .includes(OperatorType.AI_THRESHOLD.toUpperCase());
  const isValueNaN = isNaN(Number(rule.value.value));

  return isNumericType && !isSystemThreshold && !isAIThreshold && isValueNaN;
};
